import { LitElement, html, PropertyValues, TemplateResult } from 'lit';
import { property, query } from 'lit/decorators.js';
import {
  addDays,
  addMonths,
  addYears,
  endOfMonth,
  format,
  getDay,
  parse,
  startOfDay,
  subMonths,
  subYears,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import './datepicker-calendar-cell';
import { Day } from './day';
import { calendarStyles } from './view/styles';
import { CalendarEvents, DATE_FORMAT } from '../types';
import { getTimezonedCurrentDateTimestamp } from '@nirvana/shared/time';

export class DatePickerCalendarWebComponent extends LitElement {
  static get styles() {
    return calendarStyles;
  }

  /**
   * Date from. Format is Unix timestamp.
   */
  @property({ type: Number, reflect: true }) dateFrom;

  /**
   * Date to. Format is Unix timestamp.
   */
  @property({ type: Number, reflect: true }) dateTo;

  @property({ type: String }) hoveredDate;

  @property({ type: Boolean }) enableYearChange = false;
  @property({ type: String }) month = '01';
  @property({ type: Boolean }) narrow = false;
  @property({ type: Boolean }) noRange = false;
  @property({ type: Boolean }) next = false;
  @property({ type: Boolean }) prev = false;
  @property({ type: String }) year;

  @property({ type: Object })
  public get locale() {
    return this._locale ? this._locale : enUS;
  }

  public set locale(value) {
    const oldValue = this._locale;
    this._locale = value ? value : enUS;
    this.requestUpdate('locale', oldValue);
  }

  /**
   * Max date. Format is Unix timestamp
   */
  @property({ type: Number }) max;

  /**
   * Minimal date. Format is Unix timestamp
   */
  @property({ type: Number }) min;

  @property({ type: Array }) protected yearsList: Array<number> = [];
  @property({ type: Array }) protected monthsList: Array<string> = [];
  @property({ type: Array }) protected dayNamesOfTheWeek: Array<string> = [];
  @property({ type: Array }) protected daysOfMonth: Array<Array<Day | null>> = [];

  @property({ type: String, reflect: true })
  timezone = '';

  @query('#tableBody')
  tbody!: HTMLElement;

  @query('#monthName')
  monthName!: HTMLElement;

  @query('#yearName')
  yearName!: HTMLElement;

  protected _locale: Locale;
  protected currentDate: number;

  constructor() {
    super();
    this.currentDate = parseInt(format(startOfDay(Date.now()), 't'), 10);
    this.localeChanged();
    this.yearAndMonthChanged(this.year, this.month);
  }

  render(): TemplateResult {
    return html`
      <div class="calendar-container">
        <div class="header">
          ${this.renderPrevButton()}
          <div class="header-title" part="title">
            <div class="header-title--month" id="monthName">${this.computeCurrentMonthName(this.month, this.year)}</div>
            <div class="header-title--year" id="yearName">${this.renderYear()}</div>
          </div>
          <slot name="calendar-header"></slot>
          ${this.renderNextButton()}
        </div>

        <div class="table">
          <div class="thead">
            <div class="tr">${this.dayNamesOfTheWeek?.map(dayNameOfWeek => this.renderDayOfWeek(dayNameOfWeek))}</div>
          </div>
          <div class="tbody" id="tableBody">${this.daysOfMonth?.map(week => this.renderWeek(week))}</div>
        </div>
      </div>
    `;
  }

  private renderYear(): TemplateResult {
    return html`${this.year}`;
  }

  private renderDayOfWeek(dayOfWeek: string): TemplateResult {
    return html` <div class="th">${dayOfWeek}</div>`;
  }

  private renderWeek(week: Array<Day | null>): TemplateResult {
    return html` ${week.map(day => this.renderDay(day))} `;
  }

  private renderDay(day: Day | null): TemplateResult {
    return html` ${day ? this.renderDatepickerCell(day) : this.renderEmptyDatepickerCell()} `;
  }

  private renderDatepickerCell(day: Day): TemplateResult {
    const dayOfWeek = format(day.date * 1000, 'EEE');
    return html`
      <gz-datepicker-calendar-cell
        class="td ${this.tdIsEnabled(day)} day-${dayOfWeek}"
        .min="${this.min}"
        .max="${this.max}"
        .month="${this.month}"
        .hoveredDate="${this.hoveredDate}"
        .dateTo="${this.dateTo}"
        .dateFrom="${this.dateFrom}"
        .day="${day}"
        ?isCurrentDate="${this.isCurrentDate(day)}"
        @dateIsSelected="${this.handleDateSelected}"
        @dateIsHovered="${this.handleDateHovered}"
      >
      </gz-datepicker-calendar-cell>
    `;
  }

  renderEmptyDatepickerCell(): TemplateResult {
    return html` <div class="td"></div>`;
  }

  async firstUpdated(): Promise<any> {
    this.monthsList = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
    setTimeout(() => {
      this.setYears(1930, 2100);
    });
    await this.updateComplete;
  }

  updated(properties: PropertyValues): void {
    if (properties.has('locale')) {
      this.localeChanged();
      // recompute calendar rows based, since first day of the week may have changed when locale changed
      this.yearAndMonthChanged(this.year, this.month);
    }

    if (properties.has('year')) {
      this.dispatchEvent(new CustomEvent(CalendarEvents.YEAR_CHANGED, { detail: { value: this.year } }));
    }

    if (properties.has('year') || properties.has('month')) {
      this.yearAndMonthChanged(this.year, this.month);
    }

    if (properties.has('timezone')) {
      this.currentDate = getTimezonedCurrentDateTimestamp(this.timezone);
    }
  }

  private isCurrentDate(day: Day): boolean {
    const dayDate = day.date;
    return dayDate === this.currentDate;
  }

  private localeChanged(): void {
    const dayNamesOfTheWeek = [];
    for (let i = 0; i < 7; i++) {
      if (this.locale && this.locale.localize) {
        // @ts-ignore
        dayNamesOfTheWeek.push(this.locale.localize.day(i, { width: 'short' }));
      }
    }

    const firstDayOfWeek: number = this.locale.options?.weekStartsOn ? this.locale.options?.weekStartsOn : 0;
    const tmp = dayNamesOfTheWeek.slice().splice(0, firstDayOfWeek);
    const newDayNamesOfTheWeek = dayNamesOfTheWeek.slice().splice(firstDayOfWeek, dayNamesOfTheWeek.length).concat(tmp);
    this.dayNamesOfTheWeek = newDayNamesOfTheWeek;
  }

  private yearAndMonthChanged(year: number, month: string): void {
    if (year && month) {
      let monthMinus = month;
      monthMinus = monthMinus.substring(monthMinus.length - 2);
      let startDateString = `01/${monthMinus}/${year}`;
      let startDateFn = parse(startDateString, DATE_FORMAT, new Date());
      const endDateFn = endOfMonth(startDateFn);
      const endDateString = format(endDateFn, DATE_FORMAT);

      const firstDayOfWeek =
        this.locale && this.locale.options && this.locale.options.weekStartsOn ? this.locale?.options?.weekStartsOn : 0;

      const rows: Array<Array<Day | null>> = [];
      let columns: Array<Day | null> = [];

      const lastDayOfWeek = 6;

      while (startDateString !== endDateString) {
        let dayNumberFn = getDay(startDateFn) - firstDayOfWeek;
        if (dayNumberFn < 0) {
          dayNumberFn = 6;
        }

        const columnFn = new Day(startDateFn);
        columns.push(columnFn);

        if (dayNumberFn === lastDayOfWeek) {
          for (let i = columns.length; i < lastDayOfWeek + 1; i++) {
            columns.unshift(null);
          }
          rows.push(columns.slice());
          columns = [];
        }

        startDateFn = addDays(startDateFn, 1);
        startDateString = format(startDateFn, DATE_FORMAT);

        if (startDateString === endDateString) {
          const endColumnFn = new Day(startDateFn);
          columns.push(endColumnFn);
          for (let i = columns.length; i <= lastDayOfWeek; i++) {
            columns.push(null);
          }
          rows.push(columns.slice());
          columns = [];
        }
      }
      this.daysOfMonth = rows;
    }
  }

  private computeCurrentMonthName(month: string, year: number): string {
    return format(new Date(year, parseInt(month, 10) - 1), 'MMMM', {
      locale: this.locale,
    });
  }

  private tdIsEnabled(day: Day | null): string {
    return day ? 'enabled' : '';
  }

  public handleDateSelected(e: CustomEvent): void {
    const { detail } = e;
    const { date } = detail;

    if (!this.noRange) {
      if (this.dateFrom && this.dateTo) {
        this.dateFrom = date;
        this.dateTo = null;
        this.hoveredDate = null;
      } else if (!this.dateFrom || (this.dateFrom && date < this.dateFrom)) {
        this.dateFrom = date;
      } else if (!this.dateTo || (this.dateTo && date > this.dateTo)) {
        this.dateTo = date;
      }
    } else {
      this.dateFrom = date;
    }

    this.dispatchEvent(
      new CustomEvent(CalendarEvents.DATE_FROM_CHANGED, {
        detail: { value: this.dateFrom, isCurrent: this.dateFrom === this.currentDate },
        composed: true,
      })
    );

    const isCurrent = this.dateTo === this.currentDate;

    this.dispatchEvent(
      new CustomEvent(CalendarEvents.DATE_TO_CHANGED, {
        detail: {
          value: this.dateTo,
          isCurrent,
        },
      })
    );
  }

  public handleDateHovered(e: CustomEvent): void {
    if (!this.noRange) {
      this.hoveredDate = e.detail.date;
      this.classList.add('is-hovered');
      this.dispatchEvent(
        new CustomEvent(CalendarEvents.HOVERED_DATE_CHANGED, {
          detail: { value: this.hoveredDate },
        })
      );
    }
  }

  public handleNextMonth(): void {
    this.addUiElementsAnimation(['withTransition', 'moveToLeft']);

    const month = parse(this.month, 'MM', new Date());
    const monthPlusDate = addMonths(month, 1);

    const localeValue = this.locale ? this.locale : enUS;
    const monthPlusString = format(monthPlusDate, 'MM', { locale: localeValue });

    this.month = monthPlusString;
    if (this.month === '01') {
      const year = parse(this.year.toString(), 'yyyy', new Date());
      const yearPlusDate = addYears(year, 1);
      const yearPlusString = format(yearPlusDate, 'yyyy', {
        locale: localeValue,
      });
      this.year = parseInt(yearPlusString, 10);
    }
    this.dispatchEvent(new CustomEvent(CalendarEvents.NEXT_MONTH));
    this.transitionNext();
  }

  public handlePrevMonth(): void {
    this.addUiElementsAnimation(['withTransition', 'moveToRight']);

    const localeValue = this.locale ? this.locale : enUS;

    const month = parse(this.month, 'MM', new Date());
    const monthMinusDate = subMonths(month, 1);
    const monthMinusString = format(monthMinusDate, 'MM', {
      locale: localeValue,
    });

    this.month = monthMinusString;
    if (this.month === '12') {
      const year = parse(this.year.toString(), 'yyyy', new Date());
      const yearMinusDate = subYears(year, 1);
      const yearMinusString = format(yearMinusDate, 'yyyy', {
        locale: localeValue,
      });
      this.year = parseInt(yearMinusString, 10);
    }
    this.dispatchEvent(new CustomEvent(CalendarEvents.PREV_MONTH));
    this.transitionPrev();
  }

  public navigateToMonthAndYear(month: string, year: string) {
    this.month = month;
    this.year = year;
  }

  private transitionNext() {
    setTimeout(() => {
      this.removeUiElementsAnimation(['withTransition', 'moveToLeft']);
      this.addUiElementsAnimation(['moveToRight']);

      setTimeout(() => {
        this.addUiElementsAnimation(['withTransition']);
        this.removeUiElementsAnimation(['moveToRight']);
        setTimeout(() => {
          this.removeUiElementsAnimation(['withTransition']);
        }, 100);
      }, 100);
    }, 100);
  }

  private transitionPrev() {
    setTimeout(() => {
      this.tbody.classList.remove('withTransition', 'moveToRight');
      this.tbody.classList.add('moveToLeft');

      this.monthName.classList.remove('withTransition', 'moveToRight');
      this.monthName.classList.add('moveToLeft');

      this.yearName.classList.remove('withTransition', 'moveToRight');
      this.yearName.classList.add('moveToLeft');

      setTimeout(() => {
        this.addUiElementsAnimation(['withTransition']);
        this.removeUiElementsAnimation(['moveToLeft']);
        setTimeout(() => {
          this.removeUiElementsAnimation(['withTransition']);
        }, 100);
      }, 100);
    }, 100);
  }

  private removeUiElementsAnimation(animation: string[]) {
    [this.tbody, this.monthName, this.yearName].forEach(element => element.classList.remove(...animation));
  }

  private addUiElementsAnimation(animation: string[]) {
    [this.tbody, this.monthName, this.yearName].forEach(element => element.classList.add(...animation));
  }

  private setYears(from: number, to: number): void {
    const yearsList = [];
    for (let i = from; i <= to; i += 1) {
      // @ts-ignore
      yearsList.push(i);
    }
    this.yearsList = yearsList;
  }

  private renderPrevButton() {
    if (this.prev) {
      return html`<gz-icon-button
        @click="${this.handlePrevMonth}"
        class="calendar-nav-button prev"
        buttonType="square"
        icon="ds-icon-calendar_prev"
        size="24"
        fontSize="24"
        buttonColor="secondary"
      ></gz-icon-button>`;
    }
  }

  private renderNextButton() {
    if (this.next) {
      return html`<gz-icon-button
        @click="${this.handleNextMonth}"
        class="calendar-nav-button next"
        buttonType="square"
        icon="ds-icon-calendar_next"
        size="24"
        fontSize="24"
        buttonColor="secondary"
      ></gz-icon-button>`;
    }
  }
}
